import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { DateTime } from 'luxon';
import {
  IEpisodeDetailsDialogData,
  IEpisodeDetailsDialogViewData,
  IEpisodeDetailsErrors,
  IEpisodeError,
  IEpisodeOccurrence,
  IEpisodeQualityOption,
  IRecurringEpisodeViewData,
  getDuration,
  setTime,
  validateEpisodeOccurrences
} from '../../../../../core/utils/episode-details-utils';
import {
  EpisodeQuality,
  EpisodeScheduleType,
  ExtendedProgramGuideService,
  IInputEpisode,
  RecurrenceType
} from 'src/app/core/services/extended-program-guide.service';
import Debounce from 'src/app/core/utils/Debounce';
import TimeParser from 'src/app/core/utils/TimeParser';
import { formatRecurringEpisodeSchedule } from '../../extended-program-guide.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '../../../../../shared/secured-shared/dialog/dialog.service';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import getRecurringScheduleOccurrences, {
  IReplicateRecurringScheduleEpisode
} from '../../utils/getRecurringScheduleOccurrences';
import { EpisodeRecurrenceOptionsComponent } from '../../episode-recurrence-options/episode-recurrence-options.component';
// import { MatDatetimepickerInputEvent } from '@mat-datetimepicker/core';
import { Moment } from 'moment';
import moment from 'moment';

@Component({
    selector: 'app-episode-time-tab',
    templateUrl: './episode-time-tab.component.html',
    styleUrls: ['./episode-time-tab.component.scss'],
    standalone: false
})
export class EpisodeTimeTabComponent implements AfterViewInit, OnInit {
  private readonly timeParser = new TimeParser();

  @Input() public data: IEpisodeDetailsDialogData;
  @Input() public showErrors: boolean;
  @Output() viewDataUpdateEvent =
    new EventEmitter<IEpisodeDetailsDialogViewData>();
  @Output() errorUpdateEvent = new EventEmitter<IEpisodeDetailsErrors>();

  private readonly validationDebounce = new Debounce(100);

  public isUpdatingViewData = false;

  private episodeOccurrences: IEpisodeOccurrence[] = null;

  public readonly viewData: IEpisodeDetailsDialogViewData = {
    formattedRecurrence: null,
    duration: '30',
    start: {
      date: DateTime.now().toJSDate(),
      time: '00:00'
    },
    isProcessingThumbnail: false,
    isDraggingOverThumbnailElement: false,
    schedule: {
      type: EpisodeScheduleType.Definite,
      end: {
        date: DateTime.now().toJSDate(),
        time: '00:00'
      }
    }
  };

  public readonly EpisodeScheduleType = EpisodeScheduleType;

  public errors: IEpisodeDetailsErrors = {
    viewerRating: null,
    title: null,
    runsIndefinitely: null,
    description: null,
    showName: null,
    quality: null,
    duration: null,
    start: {
      date: null,
      time: null
    },
    end: {
      date: null,
      time: null
    }
  };

  public quality: IEpisodeQualityOption[] = [
    {
      label: 'Standard',
      value: EpisodeQuality.Standard
    },
    {
      label: 'HD',
      value: EpisodeQuality.HD
    }
  ];

  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly matSnackBar: MatSnackBar,
    private readonly epgService: ExtendedProgramGuideService,
    private readonly epgDialog: DialogService
  ) {}

  public ngOnInit(): void {
    this.setStartDate(this.viewData.start.date);
  }

  public ngAfterViewInit(): void {
    this.triggerSetViewData(this.data.episode);
  }

  private triggerSetViewData(value: IInputEpisode) {
    if (this.isUpdatingViewData) {
      return;
    }
    this.isUpdatingViewData = true;
    this.setViewData(value)
      .catch(
        (err): IEpisodeError => ({
          message: `${err}`
        })
      )
      .then((err) => {
        if (err !== null) {
          this.matSnackBar.open(
            `Failed to process episode information: ${err.message}`,
            'Ok'
          );
        }
      })
      .finally(() => {
        this.isUpdatingViewData = false;
      });
  }

  /**
   * Update component `viewData` property from the input episode
   * @param value Input episode
   */
  private async setViewData(value: IInputEpisode) {
    /**
     * Start time
     */
    const startTime = DateTime.fromSeconds(value.start_date);
    this.viewData.start.date = startTime.toJSDate();
    this.viewData.start.time = startTime.toFormat('hh:mm a');

    /**
     * Duration
     */
    this.viewData.duration = value.duration.toString();

    /**
     * Set schedule information
     */
    const schedule = value.schedule;
    switch (schedule.type) {
      case EpisodeScheduleType.Definite: {
        const endDate = DateTime.fromSeconds(value.start_date).plus({
          minutes: value.duration
        });
        this.viewData.schedule = {
          type: EpisodeScheduleType.Definite,
          end: {
            date: endDate.toJSDate(),
            time: endDate.toFormat('hh:mm a')
          }
        };
        break;
      }
      case EpisodeScheduleType.Recurring:
        this.viewData.schedule = {
          type: EpisodeScheduleType.Recurring,
          recurrence: schedule.recurrence,
          end: {
            date: DateTime.fromSeconds(schedule.end_date).toJSDate(),
            time: DateTime.fromSeconds(schedule.end_date).toFormat('hh:mm a')
          }
        };
        break;
      case EpisodeScheduleType.Indefinite:
        this.viewData.schedule = {
          type: EpisodeScheduleType.Indefinite
        };
        break;
    }
    return this.triggerUpdate().then(() => null);
  }

  /**
   * This function needs to be defined as a property, so we can use it as a filter function
   */
  public startDateMaterialDatePickerFilter = (value: Moment | null) => {
    const { schedule } = this.viewData;
    if (value === null || !('end' in schedule)) {
      return true;
    }
    const date = DateTime.fromJSDate(value.toDate());
    const today = DateTime.now().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    return date.toSeconds() >= today.toSeconds();
  };

  getDatesDiference() {
    let momentA = moment(this.viewData.start.date);
    let momentB = moment(this.viewData.schedule.end.date);
    return moment.duration(momentA.diff(momentB)).humanize();
  }

  getMommentFormatDate(date: Date) {
    return moment(date).format('ddd MMM, Do YYYY • hh:mm A');
  }

  public onViewDataChange() {
    this.triggerUpdate();
  }

  /**
   * This should be called whenever a view data is changed, so we can disable the save button,
   * and only enable it if everything is gtg.
   *
   * The promise returned will only be resolved after the current debounce timer is fired. Meaning,
   * if it is cleared, the promise will never be resolved.
   */
  private triggerUpdate() {
    /**
     * disable the save button until the update is completed
     */
    return this.validationDebounce.run(() => {
      this.update();
      this.changeDetectionRef.detectChanges();
    });
  }

  private update() {
    /**
     * Validate start time
     */
    const result = this.timeParser.parse(this.viewData.start.time);
    if ('message' in result) {
      this.errors.start.time = result.message;
    } else {
      this.errors.start.time = null;
      /**
       * Set start time to start date
       */
      this.viewData.start.date = setTime(
        DateTime.fromJSDate(this.viewData.start.date),
        result
      ).toJSDate();
    }

    const duration = getDuration(this.viewData.duration);
    if (typeof duration === 'string') {
      this.errors.duration = duration;
    } else {
      this.errors.duration = null;
      if (this.viewData.schedule.type !== EpisodeScheduleType.Recurring) {
        this.calculateEndTimeFromDuration(duration);
      }
    }

    /**
     * Clears "runs indefinitely" error to avoid keeping previous errors
     */
    this.errors.runsIndefinitely = null;

    /**
     * Check for overlapping episodes
     */
    this.validateOverlappingEpisodes();

    /**
     * Set recurrence formatted string
     */
    this.setRecurrenceFormattedString();

    if (this.viewDataUpdateEvent) this.viewDataUpdateEvent.emit(this.viewData);
    if (this.errorUpdateEvent) this.errorUpdateEvent.emit(this.errors);
  }

  private calculateEndTimeFromDuration(duration: number) {
    const { start, schedule } = this.viewData;
    switch (schedule.type) {
      case EpisodeScheduleType.Recurring:
      case EpisodeScheduleType.Definite: {
        const parsedStartTime = this.timeParser.parse(start.time);
        if ('message' in parsedStartTime) {
          break;
        }
        const startDate = setTime(
          DateTime.fromJSDate(start.date),
          parsedStartTime
        );
        const endDate = startDate.plus({
          minutes: duration
        });
        schedule.end.date = endDate.toJSDate();
        schedule.end.time = endDate.toFormat('hh:mm a');
        break;
      }
      case EpisodeScheduleType.Indefinite:
        break;
    }
  }

  private formatRecurringEpisodeSchedule(
    recurringData: IRecurringEpisodeViewData,
    startDate: Date,
    startTime: string
  ) {
    return formatRecurringEpisodeSchedule(recurringData, startDate, startTime);
  }

  /**
   * Set recurrence formatted string if the episode is recurring
   */
  private setRecurrenceFormattedString() {
    const schedule = this.viewData.schedule;
    if (schedule.type !== EpisodeScheduleType.Recurring) {
      this.viewData.formattedRecurrence = null;
      return;
    }
    this.viewData.formattedRecurrence = this.formatRecurringEpisodeSchedule(
      schedule,
      this.viewData.start.date,
      this.viewData.start.time
    );
  }

  private validateOverlappingEpisodes() {
    const schedule = this.viewData.schedule;
    switch (schedule.type) {
      case EpisodeScheduleType.Definite: {
        /**
         * Validate start time of the definite episodes
         */
        const startDate = DateTime.fromJSDate(this.viewData.start.date);
        const endDate = DateTime.fromJSDate(schedule.end.date);
        existingEpisodesLoop: for (const ep of this.data.episodes) {
          switch (ep.schedule.type) {
            case EpisodeScheduleType.Definite:
              const existingEpisodeStartDate = DateTime.fromSeconds(
                ep.start_date
              );
              const existingEpisodeEndDate = existingEpisodeStartDate.plus({
                minutes: ep.duration
              });
              const existingEpisodeReadableRange = this.formatEpisode(
                ep.title,
                existingEpisodeStartDate,
                existingEpisodeEndDate
              );
              if (
                startDate.toSeconds() <= existingEpisodeEndDate.toSeconds() &&
                startDate.toSeconds() >= existingEpisodeStartDate.toSeconds()
              ) {
                this.errors.start.time = `This episode start time overlaps with another episode: ${existingEpisodeReadableRange}`;
                break existingEpisodesLoop;
              } else if (
                endDate.toSeconds() >= existingEpisodeStartDate.toSeconds() &&
                startDate.toSeconds() <= existingEpisodeStartDate.toSeconds()
              ) {
                this.errors.duration = `This episode end time overlaps with another episode: ${existingEpisodeReadableRange}`;
                break existingEpisodesLoop;
              }
              break;
            case EpisodeScheduleType.Recurring:
            case EpisodeScheduleType.Indefinite:
              break;
          }
        }
        break;
      }
      case EpisodeScheduleType.Recurring:
        // ! add support for validating recurring episodes
        break;
      case EpisodeScheduleType.Indefinite:
        for (const ep of this.data.episodes) {
          if (ep.schedule.type === EpisodeScheduleType.Indefinite) {
            this.errors.runsIndefinitely =
              'There is already an indefinite episode. Only one indefinite episode per channel is allowed';
            break;
          }
        }
    }
  }

  private formatEpisode(title: string, startDate: DateTime, endDate: DateTime) {
    return `${title} starts at ${startDate.toLocaleString(
      DateTime.DATETIME_FULL
    )}, and ends at ${endDate.toLocaleString(DateTime.DATETIME_FULL)}`;
  }

  public onChangeStartTime(el: HTMLInputElement, _: Event) {
    this.setStartTime(el.value);
    this.onViewDataChange();
  }

  private setStartTime(value: string) {
    const { schedule } = this.viewData;
    const viewData = this.viewData;
    viewData.start.time = value;
    const parsed = this.timeParser.parse(value);
    if ('message' in parsed) {
      this.errors.start.time = parsed.message;
    } else {
      /**
       * Update the time of start date object
       */
      const startDate = setTime(
        DateTime.fromJSDate(viewData.start.date),
        parsed
      );
      this.errors.start.time = null;
      viewData.start.date = startDate.toJSDate();
      if ('end' in schedule) {
        // Keep the previous duration
        const n = getDuration(this.viewData.duration);
        if (typeof n === 'string') {
          return;
        } else {
          this.calculateEndTimeFromDuration(n);
        }
      }
    }
  }

  // public onChangeStartDate(el: Moment | MatDatetimepickerInputEvent<Moment>) {
  //   let newDate: Moment | null = 'value' in el ? el.value : el;
  //   if (newDate === null) {
  //     this.errors.start.date =
  //       'Start date can be selected using the date picker';
  //   } else {
  //     this.errors.start.date = null;
  //     this.setStartDate(newDate.toDate());
  //     //setStartTime to 00:00
  //     //if duration its not null change end time
  //   }
  //   this.onViewDataChange();
  // }

  getUtcDate(date: Date) {
    var utc_date = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
    return new Date(utc_date);
  }

  getUtcTime(date: Date) {
    var utc_time = date.getUTCHours() + ':' + date.getUTCMinutes();
    return utc_time;
  }

  private setStartDate(value: Date) {
    const { schedule } = this.viewData;
    const viewData = this.viewData;
    const hoursAndMinutes = value.getHours() + ':' + value.getMinutes();
    this.viewData.start.date = value;
    this.viewData.start.time = hoursAndMinutes;
    const parsedStartTime = this.timeParser.parse(viewData.start.time);
    if ('message' in parsedStartTime) {
      /**
       * The caller of setStartTime should do the validation
       */
      return;
    }
    if (!('end' in schedule)) {
      console.error('setStartDate: Unexpected schedule type: %o', schedule);
      return;
    }
    // Keep the previous duration
    const n = getDuration(this.viewData.duration);
    if (typeof n === 'string') {
      return;
    } else {
      this.calculateEndTimeFromDuration(n);
    }
  }

  public onChangeDuration(e: HTMLInputElement) {
    this.setDuration(e.value);
    this.onViewDataChange();
  }

  private setDuration(value: string) {
    const n = getDuration(value);
    if (typeof n === 'string') {
      this.errors.duration = n;
      this.viewData.duration = value;
      return null;
    } else {
      this.errors.duration = null;
      this.viewData.duration = n.toString();
      this.calculateEndTimeFromDuration(n);
    }
    return n;
  }

  public onChangeRunsIndefinitely(e: MatCheckboxChange) {
    if (e.checked) {
      this.viewData.schedule = {
        type: EpisodeScheduleType.Indefinite
      };
    } else {
      /**
       * We can put anything here since `duration` will be updated by `calculateEndTimeFromDuration` call
       */
      this.viewData.schedule = {
        type: EpisodeScheduleType.Definite,
        end: {
          date: new Date(0),
          time: '00:00'
        }
      };
    }
    this.onViewDataChange();
  }

  /**
   * This function needs to be defined as a property, so we can use it as a filter function
   */
  public endDateMaterialDatePickerFilter = (value: Date | null) => {
    if (value === null) {
      return false;
    }
    const startDate = DateTime.fromJSDate(this.viewData.start.date).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    const endDate = DateTime.fromJSDate(value).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    return endDate.toSeconds() >= startDate.toSeconds();
  };

  public onChangeEndTime(el: HTMLInputElement) {
    this.setEndTime(el.value);
    this.onViewDataChange();
  }

  public onChangeEndDate(el: Date | MatDatepickerInputEvent<Date>) {
    let newDate: Date | null = 'value' in el ? el.value : el;
    if (newDate === null) {
      this.errors.end.date = 'End date can be selected using the date picker';
    } else {
      this.errors.end.date = null;
      this.setEndDate(newDate);
    }
    this.onViewDataChange();
  }

  private setEndTime(value: string) {
    const { schedule } = this.viewData;
    if (!('end' in schedule)) {
      return;
    }
    schedule.end.time = value;
    /**
     * Parse new end time
     */
    const parsedEndTime = this.timeParser.parse(value);
    /**
     * Set end time error
     */
    if ('message' in parsedEndTime) {
      this.errors.end.time = parsedEndTime.message;
    } else {
      this.errors.end.time = null;
      /**
       * Set the time of the end date
       */
      const endDate = setTime(
        DateTime.fromJSDate(schedule.end.date),
        parsedEndTime
      );
      /**
       * Update end date time
       */
      schedule.end.date = endDate.toJSDate();
      const duration = endDate.diff(
        DateTime.fromJSDate(this.viewData.start.date),
        'minutes'
      ).minutes;
      if (duration < 0) {
        this.errors.end.time = 'End time should be greater than start date';
      } else {
        this.errors.end.time = null;
        /**
         * Recalculate duration based on new end time
         */
        this.setDuration(duration.toString());
        /**
         * Set end time again, since `setDuration` might call `calculateEndTimeFromDuration`.
         */
        schedule.end.time = value;
      }
    }
  }

  public onEpisodeScheduleTypeSelectionChange(
    event: MatSelectChange,
    el: MatSelect
  ) {
    switch (event.value as EpisodeScheduleType) {
      case EpisodeScheduleType.Definite:
        this.onChangeEpisodeSchedule({
          type: EpisodeScheduleType.Definite,
          end: {
            date: DateTime.now().toJSDate(),
            time: '00:00'
          }
        });
        break;
      case EpisodeScheduleType.Recurring:
        el.value = EpisodeScheduleType.Definite;
        this.openSetEpisodeRecurrenceDialog();
        break;
      /**
       * Does nothing, this option should be disabled
       */
      case EpisodeScheduleType.Indefinite:
        break;
    }
  }

  private openSetEpisodeRecurrenceDialog() {
    const startDate = DateTime.fromJSDate(this.viewData.start.date);
    const duration = getDuration(this.viewData.duration);

    if (typeof duration === 'string') {
      this.matSnackBar.open('Invalid duration', 'Ok');
      return;
    }

    this.epgDialog
      .openDialog({
        title: 'Set Recurrence',
        content: EpisodeRecurrenceOptionsComponent,
        data: {
          startDate: startDate.toJSDate(),
          startTime: this.viewData.start.time,
          duration,
          episodes: this.data.episodes,
          schedule: {
            type: EpisodeScheduleType.Recurring,
            end: {
              date: DateTime.now().plus({ weeks: 2 }).toJSDate(),
              time: '00:00'
            },
            recurrence: {
              type: RecurrenceType.Week,
              week_days: [startDate.weekday],
              repeat: 1
            }
          }
        }
      })
      .afterClosed()
      .subscribe((selectedOptions) => {
        if (selectedOptions === null) {
          return;
        }
        this.viewData.start.date = selectedOptions.startDate;
        this.onChangeEpisodeSchedule(selectedOptions.schedule);
      });
  }

  public onChangeEpisodeSchedule(
    newSchedule: IEpisodeDetailsDialogViewData['schedule']
  ) {
    this.viewData.schedule = newSchedule;
    this.onViewDataChange();
    switch (newSchedule.type) {
      case EpisodeScheduleType.Definite:
      case EpisodeScheduleType.Indefinite:
        break;
      case EpisodeScheduleType.Recurring: {
        const duration = getDuration(this.viewData.duration);
        if (typeof duration === 'string') {
          break;
        }
        const recurringEpisodeSchedule: IReplicateRecurringScheduleEpisode = {
          duration,
          start_date: DateTime.fromJSDate(this.viewData.start.date).toSeconds()
        };
        const episodeOccurrences = getRecurringScheduleOccurrences(
          recurringEpisodeSchedule,
          {
            type: EpisodeScheduleType.Recurring,
            end_date: DateTime.fromJSDate(newSchedule.end.date).toSeconds(),
            recurrence: newSchedule.recurrence
          }
        );
        if ('type' in episodeOccurrences) {
          console.log(episodeOccurrences);
          break;
        }
        for (const occurrence of episodeOccurrences) {
          console.log(
            'start = %s, end = %s',
            occurrence.startDate.toLocaleString(DateTime.DATETIME_FULL),
            occurrence.endDate.toLocaleString(DateTime.DATETIME_FULL)
          );
        }
        console.log(
          validateEpisodeOccurrences(
            this.episodeOccurrences,
            recurringEpisodeSchedule
          )
        );
        break;
      }
    }
  }

  public onRecurringOptionClick() {
    if (this.viewData.schedule.type === EpisodeScheduleType.Recurring) {
      this.openSetEpisodeRecurrenceDialog();
    }
  }

  private setEndDate(date: Date) {
    const { schedule } = this.viewData;

    if (schedule.type !== EpisodeScheduleType.Definite) {
      return;
    }

    const endTime = this.timeParser.parse(schedule.end.time);

    if ('message' in endTime) {
      this.errors.end.time = endTime.message;
    } else {
      const endDate = setTime(DateTime.fromJSDate(date), endTime);
      schedule.end.date = endDate.toJSDate();
      const duration = endDate.diff(
        DateTime.fromJSDate(this.viewData.start.date),
        'minutes'
      ).minutes;

      if (duration < 0.0) {
        this.errors.end.date = 'End date should be greater than start date';
      } else {
        this.errors.end.date = null;

        /**
         * Set the new duration based on the selected end date
         */
        this.setDuration(duration.toString());
      }
    }
  }
}
